/**
 * Pure model for chat @-mentions (attach a source section/table as context).
 *
 * The writer types ``@`` in the CommandRail input and walks a 3-stage
 * picker: asset type (Section/Table) → source file → section title /
 * table caption. The picked asset becomes:
 *   1. a compact citation marker in the input text — ``@[1]`` — that reads
 *      like a footnote, and
 *   2. a structured ``MentionAttachment`` sent alongside the message; the
 *      server resolves it to content and injects a matching numbered block
 *      (``1. TABLE "…"``) before the agent runs.
 *
 * ``renumberMentions`` keeps the marker numbers, the chip badges, the
 * attachment array order, and the server block index all in agreement.
 *
 * Everything here is pure string/array logic (no React, no IO) so the
 * trigger detection, token grammar, and reconciliation are unit-testable
 * in isolation. The stage state machine itself lives in CommandRail —
 * these helpers are its transitions' building blocks.
 */

import type { MentionAttachment } from "../types";

export type { MentionAttachment };

export type MentionStage = "type" | "file" | "asset";

export interface MentionTrigger {
  /** Index of the ``@`` character in the input value. */
  triggerIndex: number;
  /** Text typed between the ``@`` and the caret — the live filter query. */
  query: string;
}

/** Token regex — the inline citation marker ``@[<n>]`` where ``n`` is the
 *  1-based citation number. The ``@[`` prefix keeps ``detectMentionTrigger``
 *  from re-opening the picker on a completed token (its ``startsWith("[")``
 *  guard). The kind/label no longer live in the text — they come from the
 *  structured attachment (and the numbered attachment block the server
 *  injects); the marker only carries the number that ties the two together. */
const TOKEN_RE = /@\[(\d+)\]/g;

export interface MentionToken {
  start: number;
  end: number;
  /** 1-based citation number (matches the chip badge + server block index). */
  number: number;
}

/** Compact input-text citation marker for citation number ``n``. */
export function mentionToken(n: number): string {
  return `@[${n}]`;
}

/**
 * Detect a live mention trigger at the caret.
 *
 * A trigger is a bare ``@`` at start-of-input or after whitespace, with
 * the caret positioned after it and no ``]``/newline/second-``@`` in
 * between (those mean the writer moved on or the token is complete).
 */
export function detectMentionTrigger(value: string, caret: number): MentionTrigger | null {
  if (caret < 1 || caret > value.length) return null;
  const at = value.lastIndexOf("@", caret - 1);
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(value[at - 1])) return null; // mid-word @ (emails etc.)
  const between = value.slice(at + 1, caret);
  if (/[\]\n@]/.test(between)) return null;
  if (between.startsWith("[")) return null; // completed/in-progress token, not a query
  return { triggerIndex: at, query: between };
}

/** All citation markers in the text, in text order. */
export function findMentionTokens(text: string): MentionToken[] {
  const out: MentionToken[] = [];
  TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null = TOKEN_RE.exec(text);
  while (m !== null) {
    out.push({
      start: m.index,
      end: m.index + m[0].length,
      number: Number(m[1]),
    });
    m = TOKEN_RE.exec(text);
  }
  return out;
}

/**
 * Replace the ``@query`` span with a citation marker ``@[n]`` (plus a
 * trailing space) and report the new caret position. ``n`` is the marker's
 * temporary number; ``renumberMentions`` normalises it to text order after.
 */
export function insertMentionToken(
  value: string,
  triggerIndex: number,
  caret: number,
  n: number,
): { next: string; caret: number } {
  const token = `${mentionToken(n)} `;
  const next = value.slice(0, triggerIndex) + token + value.slice(caret);
  return { next, caret: triggerIndex + token.length };
}

/** Strip the typed filter query but keep the ``@`` (stage advance). */
export function clearMentionQuery(
  value: string,
  triggerIndex: number,
  caret: number,
): { next: string; caret: number } {
  const next = value.slice(0, triggerIndex + 1) + value.slice(caret);
  return { next, caret: triggerIndex + 1 };
}

/**
 * The single source of truth that keeps four representations in agreement —
 * the inline ``@[n]`` markers, the chip-row badges, the ``mentionAttachments``
 * array order, and the server's numbered attachment block. In one pass it:
 *   - **reconciles**: an attachment whose marker was hand-deleted is dropped
 *     (its marker no longer references it), and
 *   - **renumbers**: the surviving markers become ``@[1..N]`` in TEXT order,
 *     and the attachment array is reordered to match (so ``index + 1`` == the
 *     citation number everywhere).
 *
 * Maps each marker to its attachment by the marker's CURRENT number
 * (``attachments[number - 1]``), relying on the invariant "number == array
 * index + 1" that every caller re-establishes by running this after its edit.
 * ``changed`` is false when the input is already canonical, so handlers can
 * skip writing state and never fight the caret during ordinary typing.
 */
export function renumberMentions(
  value: string,
  attachments: MentionAttachment[],
): { value: string; attachments: MentionAttachment[]; changed: boolean } {
  const tokens = findMentionTokens(value);
  const reordered: MentionAttachment[] = [];
  let next = "";
  let cursor = 0;
  let n = 1;
  for (const token of tokens) {
    const att = attachments[token.number - 1];
    if (att) reordered.push(att); // orphaned markers contribute no attachment
    next += value.slice(cursor, token.start) + mentionToken(n);
    cursor = token.end;
    n += 1;
  }
  next += value.slice(cursor);
  const changed =
    next !== value ||
    reordered.length !== attachments.length ||
    reordered.some((a, i) => a !== attachments[i]);
  return { value: next, attachments: reordered, changed };
}

/**
 * Atomic token delete: when the caret sits exactly at (or just after,
 * allowing the inserted trailing space) a token's end, Backspace
 * removes the whole token instead of eating it character by character.
 * Returns null when the caret isn't at a token boundary.
 */
export function deleteTokenAtCaret(
  value: string,
  caret: number,
): { next: string; caret: number; token: MentionToken } | null {
  for (const token of findMentionTokens(value)) {
    const endWithSpace = value[token.end] === " " ? token.end + 1 : token.end;
    if (caret === token.end || caret === endWithSpace) {
      const next = value.slice(0, token.start) + value.slice(endWithSpace);
      return { next, caret: token.start, token };
    }
  }
  return null;
}

/** Remove the marker with citation number ``n`` (chip ✕ path). Caller then
 *  runs ``renumberMentions`` to close the gap left in the numbering. */
export function removeTokenByNumber(value: string, n: number): string {
  for (const token of findMentionTokens(value)) {
    if (token.number === n) {
      const endWithSpace = value[token.end] === " " ? token.end + 1 : token.end;
      return value.slice(0, token.start) + value.slice(endWithSpace);
    }
  }
  return value;
}

/** Case-insensitive substring filter used by every picker stage. */
export function filterByQuery<T>(items: T[], query: string, key: (item: T) => string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((item) => key(item).toLowerCase().includes(needle));
}

/**
 * Natural, case-insensitive comparator for the picker lists. ``numeric``
 * makes "Doc 2" precede "Doc 10" (not lexical "10" < "2"), and
 * ``sensitivity: "base"`` folds case/accents so "apple" sorts next to
 * "Apple". Used to order the file and section/table stages alphanumerically.
 */
export function compareAlphanumeric(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Resolve the span to replace when inserting a picked attachment.
 *
 * The picker now persists after the ``@``-trigger pattern breaks (a stray
 * ``]``, a space, the caret wandering off), so the stored ``triggerIndex`` /
 * ``caretEnd`` can drift out of sync with the text. Rather than trust them,
 * recompute at select time:
 *   1. a live trigger at the caret → replace ``@query`` (the normal case);
 *   2. no live trigger but the original ``@`` still sits at
 *      ``fallbackTriggerIndex`` and precedes the caret → replace from that
 *      ``@`` to the caret;
 *   3. otherwise (the ``@`` is gone) → insert at the caret, removing nothing.
 */
export function resolveMentionSpan(
  value: string,
  liveCaret: number,
  fallbackTriggerIndex: number,
): { triggerIndex: number; caretEnd: number } {
  const trig = detectMentionTrigger(value, liveCaret);
  if (trig) return { triggerIndex: trig.triggerIndex, caretEnd: liveCaret };
  if (value[fallbackTriggerIndex] === "@" && fallbackTriggerIndex <= liveCaret) {
    return { triggerIndex: fallbackTriggerIndex, caretEnd: liveCaret };
  }
  return { triggerIndex: liveCaret, caretEnd: liveCaret };
}

/** Dedupe key — one attachment per (type, file, label) per message. */
export function attachmentKey(a: MentionAttachment): string {
  return `${a.assetType} ${a.fileId} ${a.label}`;
}

/**
 * Advance a highlighted-index by delta (+1 for ArrowDown, -1 for
 * ArrowUp), wrapping at the list boundaries. Returns the original
 * index when the list is empty (caller's job to not fire nav
 * against nothing). Pure.
 */
export function nextHighlightedIndex(currentIndex: number, delta: 1 | -1, totalItems: number): number {
  if (totalItems <= 0) return currentIndex;
  const raw = currentIndex + delta;
  // JavaScript's modulo can go negative; handle explicitly.
  return (((raw % totalItems) + totalItems) % totalItems);
}
